import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { InquiryForm } from "./InquiryForm";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, Sparkles, UserRound } from "lucide-react";

export function Navbar() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/programs", label: t.nav.programs },
    { to: "/summer-camp", label: t.nav.summer },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/about", label: t.nav.about },
    { to: "/educators", label: t.nav.educators },
    { to: "/contact", label: t.nav.contact },
  ];

  const overHero = ["/", "/summer-camp"].includes(loc.pathname) && !scrolled;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${scrolled ? "bg-cream/90 backdrop-blur-xl shadow-[0_4px_30px_rgb(0,0,0,0.06)] border-b border-black/5" : "bg-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="leading-tight">
            <div className={`font-heading font-bold text-lg transition-colors ${overHero ? "text-white" : "text-navy"}`}>Ainalem</div>
            <div className={`text-[10px] tracking-widest uppercase font-semibold transition-colors ${overHero ? "text-mustard" : "text-mustard-dark"}`}>Family Childcare</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => {
            const active = loc.pathname === l.to;
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "text-coral bg-coral/10" : overHero ? "text-white/90 hover:text-white hover:bg-white/10" : "text-navy/80 hover:text-navy hover:bg-navy/5"}`}>
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-1.5">
          <LanguageSwitcher compact light={overHero} />
          <Button variant="ghost" size="sm" onClick={() => nav(user ? (user.role === "admin" ? "/admin" : "/portal") : "/login")}
            data-testid="nav-portal-btn" className={`hidden sm:flex gap-1.5 rounded-full transition-colors ${overHero ? "text-white hover:bg-white/10 hover:text-white" : "text-navy hover:bg-navy/5"}`}>
            <UserRound className="h-4 w-4" /> {t.nav.portal}
          </Button>
          <InquiryForm type="tour" trigger={
            <Button data-testid="nav-book-tour-btn" className="hidden sm:flex rounded-full bg-coral hover:bg-coral-dark text-white gap-1.5 shadow-md">
              <Sparkles className="h-4 w-4" /> {t.nav.bookTour}
            </Button>
          } />

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className={`lg:hidden transition-colors ${overHero ? "text-white hover:bg-white/10" : "text-navy"}`} data-testid="mobile-menu-btn"><Menu /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream w-[85vw] max-w-sm">
              <div className="flex flex-col gap-1 mt-8">
                {links.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className="px-4 py-3 rounded-xl text-navy font-semibold hover:bg-navy/5">{l.label}</Link>
                ))}
                <button onClick={() => { setMobileOpen(false); nav(user ? (user.role === "admin" ? "/admin" : "/portal") : "/login"); }}
                  className="px-4 py-3 rounded-xl text-navy font-semibold hover:bg-navy/5 text-left" data-testid="mobile-portal-btn">{t.nav.portal}</button>
                <div className="px-4 mt-4">
                  <InquiryForm type="tour" trigger={<Button className="w-full rounded-full bg-coral hover:bg-coral-dark text-white" data-testid="mobile-book-tour-btn">{t.nav.bookTour}</Button>} />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
